import { Spinner } from "@npco/zeller-design-system";
import * as Illustration from "@npco/zeller-design-system/illustration";
import { ADT } from "pb.adt";

import { formatCurrency } from "../../../../@lib-i18n";
import { Layout } from "../../shared/layout";
import { NetworkErrorPrompt } from "../../shared/network-error-prompt";
import { Prompt } from "../../shared/prompt";
import type { State as Page } from "../refund-matched/state";
import { RefundTransaction } from "./refund-transaction";
import { SelectTransaction } from "./select-transaction";

export const defaultLayoutProps = {
	actionsStyle: "full-width",
	logo: true,
} satisfies Layout.Props;

export namespace render {
	export type Props = { page: Page | Error };
}

export function render({ page }: render.Props) {
	if (page instanceof Error) {
		return (
			<Layout {...defaultLayoutProps}>
				<Prompt
					image={<Illustration.Error />}
					title="Error"
					message={page.message}
				/>
			</Layout>
		);
	}

	return ADT.switch(page, {
		Initialising: () => (
			<Layout {...defaultLayoutProps}>
				<Prompt image={<Spinner />} title="Connecting" />
			</Layout>
		),
		// transaction states
		AwaitAccountSelection: ({ onClose }) => (
			<Layout {...defaultLayoutProps} onClose={onClose}>
				<Prompt
					image={<Illustration.TerminalT2Application />}
					title="Waiting for Account selection"
				/>
			</Layout>
		),
		AwaitApplicationSelection: ({ onClose }) => (
			<Layout {...defaultLayoutProps} onClose={onClose}>
				<Prompt
					image={<Illustration.TerminalT2Application />}
					title="Waiting for Card Application selection"
				/>
			</Layout>
		),
		AwaitClientRefund: () => (
			<Layout {...defaultLayoutProps}>
				<Prompt image={<Spinner />} title="Processing Refund" />
			</Layout>
		),
		AwaitPinEntry: ({ onClose }) => (
			<Layout {...defaultLayoutProps} onClose={onClose}>
				<Prompt
					image={<Illustration.TerminalT2Pin />}
					title="Waiting for Card PIN entry"
				/>
			</Layout>
		),
		AwaitProcessing: () => (
			<Layout {...defaultLayoutProps}>
				<Prompt image={<Spinner />} title="Processing Payment" />
			</Layout>
		),
		AwaitSignature: ({ onClose }) => (
			<Layout {...defaultLayoutProps} onClose={onClose}>
				<Prompt
					image={<Illustration.TerminalT2Signature />}
					title="Waiting for signature entry"
				/>
			</Layout>
		),
		AwaitTransaction: () => (
			<Layout {...defaultLayoutProps}>
				<Prompt image={<Spinner />} title="Locating Transaction" />
			</Layout>
		),
		DisplayPaymentMessage: ({ onClose }) => (
			<Layout {...defaultLayoutProps} onClose={onClose}>
				<Prompt
					image={<Illustration.TerminalT2Application />}
					title="Card Payment Message pending"
				/>
			</Layout>
		),
		SignatureSubmitted: ({ signature, onClose, onApprove, onDecline }) => {
			const asset = signature ? (
				<img
					alt="customer signature"
					className="dark:invert w-[180px] h-[180px]"
					src={signature}
				/>
			) : (
				<Illustration.TerminalT2Signature loop={false} />
			);
			return (
				<Layout
					{...defaultLayoutProps}
					onClose={onClose}
					actions={[
						{ label: "Approve", onClick: onApprove, primary: true },
						{ label: "Decline", onClick: onDecline, primary: false },
					]}
				>
					<Prompt image={asset} title="Verify Signature" />
				</Layout>
			);
		},
		// response states
		Approved: ({ currency, totalCents, onClose }) => (
			<Layout
				{...defaultLayoutProps}
				timeout={{ onTimeout: onClose }}
				actions={[{ label: "Done", onClick: onClose, primary: true }]}
			>
				<Prompt
					image={<Spinner result="ok" />}
					title={`${formatCurrency({ amountInCents: totalCents, currency })} Refunded`}
				/>
			</Layout>
		),
		Declined: ({ title, subtitle, message, onRetry, onCancel }) => (
			<Layout
				{...defaultLayoutProps}
				timeout={{ onTimeout: onCancel }}
				actions={[
					{ label: "Try Again", onClick: onRetry, primary: true },
					{ label: "Cancel", onClick: onCancel },
				]}
			>
				<Prompt
					image={<Spinner result="error" />}
					title={title}
					subtitle={subtitle}
					message={message}
				/>
			</Layout>
		),
		SelectTransaction: (props) => <SelectTransaction {...props} />,
		RefundTransaction: (props) => <RefundTransaction {...props} />,
		ErrorDisconnected: ({ onClose, onRetry }) => (
			<Layout
				{...defaultLayoutProps}
				timeout={{ onTimeout: onClose }}
				actions={[
					{ label: "Try Again", onClick: onRetry, primary: true },
					{ label: "Close", onClick: onClose },
				]}
			>
				<Prompt
					image={<Illustration.Error />}
					title="Terminal Error"
					message="The Terminal has lost connection with this POS register. Please check the connection and try again. If this is the first time you're setting up the register, you may need to set up the terminal connection first."
				/>
			</Layout>
		),
		ErrorFailedToConnectToTerminal: ({ onClose, onRetry }) => (
			<Layout
				{...defaultLayoutProps}
				timeout={{ onTimeout: onClose }}
				actions={[
					{ label: "Try Again", onClick: onRetry, primary: true },
					{ label: "Close", onClick: onClose },
				]}
			>
				<Prompt
					image={<Illustration.ErrorBoatSinking loop={false} />}
					title="Failed to connect to terminal"
					message="Please check that your Zeller Terminal is connected to the Internet and try again."
				/>
			</Layout>
		),
		ErrorInactivityTimeout: ({ onClose, onRetry }) => (
			<Layout
				{...defaultLayoutProps}
				timeout={{ onTimeout: onClose }}
				actions={[
					{ label: "Try Again", onClick: onRetry, primary: true },
					{ label: "Close", onClick: onClose },
				]}
			>
				<Prompt
					image={<Illustration.ErrorBoatSinking loop={false} />}
					title="Inactivity Timeout"
					message="The user failed to complete action. Please ask customer to try again."
				/>
			</Layout>
		),
		ErrorNetwork: ({ onClose, onRetry }) => (
			<Layout
				{...defaultLayoutProps}
				actionsStyle="full-width"
				onClose={onClose}
				timeout={{ onTimeout: onClose }}
				actions={[{ label: "Try Again", onClick: onRetry, primary: false }]}
			>
				<NetworkErrorPrompt />
			</Layout>
		),
		ErrorOperation: ({ onClose }) => (
			<Layout
				{...defaultLayoutProps}
				timeout={{ onTimeout: onClose }}
				actions={[{ label: "Close", onClick: onClose }]}
			>
				<Prompt
					image={<Illustration.Error />}
					title="Operation Failed"
					message="An unrecoverable error occurred when processing this refund. Please try again."
				/>
			</Layout>
		),
		ErrorTerminalBusy: ({ onClose, onRetry }) => (
			<Layout
				{...defaultLayoutProps}
				timeout={{ onTimeout: onClose }}
				actions={[
					{ label: "Try Again", onClick: onRetry, primary: true },
					{ label: "Close", onClick: onClose },
				]}
			>
				<Prompt
					image={<Illustration.ErrorBoatSinking loop={false} />}
					title="Terminal Busy"
					message="The terminal is currently busy. Please check the Terminal and try again."
				/>
			</Layout>
		),
		ErrorTransactionNotFound: ({ onClose }) => (
			<Layout
				{...defaultLayoutProps}
				actions={[{ label: "Close", onClick: onClose, primary: true }]}
			>
				<Prompt
					image={<Illustration.ErrorQuestionMark />}
					title="Transaction Not Found"
					message={[
						"This transaction could not be found in Zeller.",
						"Please contact Zeller Support if this keeps happening.",
					]}
				/>
			</Layout>
		),
	});
}
